import { Controller } from "@hotwired/stimulus"

// Show-page direct text editing (card #2079 Phase 1) — the show-page twin of
// the build editor's startInlineEdit (deck_editor_controller.js), but writing
// through the narrow PATCH /decks/:id/element/:slide_index/:element_index
// endpoint instead of holding deck_json in memory: the show page renders
// server-side only, so there is nothing here to keep a working copy of.
//
// This controller is only ever ATTACHED (see decks/_viewer.html.erb) when the
// server has decided the render is editable, and even then it does nothing
// until the viewer actually contains an element carrying data-deck-editable
// (decks/slides/_canvas.html.erb only emits that attribute for an eligible
// element — DecksHelper#deck_element_editable?). A read-only viewer's DOM has
// no such attribute anywhere, so there is nothing for the menu below to ever
// address.
//
// MENU-GATED ENTRY POINT (deck #28 incident, 2026-07-08, second round): this
// used to bind straight onto `contextmenu` on each editable element (a first
// pass at fixing the incident below). Jay tested that live and called it
// wrong — a right-click must show a MENU first, with Edit as one option, not
// jump straight into edit mode. DeckElementContextMenuController now owns the
// contextmenu interception + the Edit/Add Feedback menu, and calls
// #openEditorFor(target) directly (a plain method call, not an event) when
// "Edit" is chosen. Everything from here down — the textarea, the commit,
// the blank-content guard — is UNCHANGED from that first pass.
//
// ORIGINAL INCIDENT: this controller used to bind on a plain `click`, so any
// incidental left click anywhere on a large html/chart slide (scrolling past
// it, reviewing it) opened an edit textarea pre-filled with empty text, and
// blur unconditionally PATCHed that blank content — production's actual
// blank-slide cause, confirmed via PaperTrail.
export default class extends Controller {
  static values = { deckId: Number }

  connect() {
    this._activeEditor = null
  }

  disconnect() {
    this._removeActiveEditor()
  }

  // Public entry point, called by DeckElementContextMenuController#edit once
  // the reviewer has explicitly chosen "Edit" from the right-click menu — NOT
  // wired to any DOM event itself. The caller is responsible for its own
  // event handling (preventDefault/stopPropagation/closing the menu) before
  // getting here; this method only knows how to open an editor on a target.
  openEditorFor(target) {
    // Only one inline editor open at a time — choosing Edit again elsewhere
    // while one is open just does nothing (its own blur handler commits
    // first).
    if (this._activeEditor) return
    if (!target) return

    const { slideIndex, elementIndex, elementKind } = target.dataset
    if (slideIndex === undefined || elementIndex === undefined) return

    this._openEditor(target, slideIndex, elementIndex, elementKind)
  }

  _openEditor(target, slideIndex, elementIndex, elementKind) {
    const parent = target.offsetParent || target.parentElement

    // Position/size read straight from the target's own AUTHORED geometry —
    // the literal inline left/top/width/height px values _canvas.html.erb
    // writes from element["x"/"y"/"w"/"h"] (see the wrapper divs
    // deck_element_edit_attrs attaches to). The deck viewer scales the WHOLE
    // slide down to fit the screen via a CSS transform on this same
    // offsetParent (.airdrop-slide-scale > .airdrop-slide { transform:
    // scale(...) } in application.css). getBoundingClientRect() returns
    // POST-transform screen pixels — using those to size a new element that
    // itself lives INSIDE the same transformed parent double-applies the
    // scale (the textarea rendered at roughly scale² of the intended size,
    // i.e. visibly narrower than the real text box). Reading the target's
    // own local (pre-transform) style values instead puts the textarea in
    // exactly the same coordinate space as the element it's editing, so the
    // SAME ancestor transform scales it once and the boxes line up exactly.
    const elementLeft = parseFloat(target.style.left) || 0
    const elementTop = parseFloat(target.style.top) || 0
    const elementWidth = parseFloat(target.style.width) || 0
    const elementHeight = parseFloat(target.style.height) || 0

    // The "roomier" text/html minimums below are a screen-px comfort size, so
    // they need the local<->screen scale factor applied in reverse. offsetWidth
    // is the pre-transform layout width (a CSS transform never changes it), so
    // this ratio recovers the live scale factor without parsing the transform
    // matrix directly.
    const parentRect = parent.getBoundingClientRect()
    const scale = parent.offsetWidth > 0 ? parentRect.width / parent.offsetWidth : 1

    // Headline: a snug inline overlay matching the element's own box exactly
    // (mirrors startInlineEdit). Text/html: a roomier surface — Vela's design
    // (activity #8822) calls these out as "NOT cramped into the box", so the
    // textarea grows to a sensible minimum rather than tracking the (often
    // small) rendered box height.
    const isHeadline = elementKind === "headline"
    const minWidth = 220 / scale
    const minHeight = 120 / scale
    const width = Math.max(elementWidth, isHeadline ? elementWidth : minWidth)
    const height = isHeadline ? elementHeight : Math.max(elementHeight, minHeight)

    // Stat visual (card #2407): `target` is the WHOLE stat card (value +
    // label divs), carrying the geometry edit_attrs needs (see
    // _canvas.html.erb) — but the editable CONTENT is only the value, never
    // the label sitting beside it. Reading/writing `target.textContent`
    // directly would seed the textarea with "604Place Visits" concatenated,
    // and committing would blow away the label div entirely (textContent=
    // replaces all children). `valueNode` is the SAME element for every
    // other editable kind (a plain div with no stat children), so this stays
    // a no-op everywhere else.
    const valueNode = target.querySelector(".airdrop-el--stat-value") || target

    const textarea = document.createElement("textarea")
    textarea.value = valueNode.textContent.trim()
    textarea.style.cssText = [
      "position:absolute",
      `left:${elementLeft}px`,
      `top:${elementTop}px`,
      `width:${width}px`,
      `height:${height}px`,
      "font:inherit",
      "line-height:1.2",
      "color:#111827",
      "background:rgba(255,255,255,0.96)",
      "border:2px dashed #003d68",
      "border-radius:2px",
      "outline:none",
      "resize:none",
      "padding:4px 6px",
      "box-sizing:border-box",
      "z-index:200",
    ].join(";")

    parent.appendChild(textarea)

    // Optional reason field (card #2324) -- kept separate from the rendered
    // content itself, so "why" this value was overridden never pollutes what
    // actually shows on the slide. Pre-filled from data-override-reason
    // (DecksHelper#deck_element_edit_attrs) so re-editing content on an
    // already-overridden element doesn't silently drop its existing reason.
    // Positioned directly below the content textarea, in the SAME local
    // (pre-transform) coordinate space, so it scales and lines up with it.
    //
    // LABEL (card #2324 follow-up, Jay live-tested): the field used to be a
    // bare input with only a placeholder -- which reads as a random unlabelled
    // extra box the instant any text is typed into it (a placeholder vanishes
    // on input, taking the only clue of what the field IS with it). A real
    // `<label for>` above it gives the field its own heading + accessible
    // name, exactly like every other WIN form field (form-label-basic), so it
    // reads as a deliberate second field, not clutter. Built by hand (no Rails
    // form helper here -- this whole editor is raw DOM over a canvas overlay)
    // but the for/id association is real, not just visual.
    const reasonLabelHeight = 16
    const reasonLabel = document.createElement("label")
    const reasonInputId = `deck-override-reason-${slideIndex}-${elementIndex}`
    reasonLabel.setAttribute("for", reasonInputId)
    reasonLabel.textContent = "Reason (optional)"
    reasonLabel.style.cssText = [
      "position:absolute",
      `left:${elementLeft}px`,
      `top:${elementTop + height + 6}px`,
      `width:${width}px`,
      "font:600 11px sans-serif",
      "color:#475569",
      "z-index:200",
    ].join(";")
    parent.appendChild(reasonLabel)

    const reasonInput = document.createElement("input")
    reasonInput.type = "text"
    reasonInput.id = reasonInputId
    reasonInput.value = target.dataset.overrideReason || ""
    reasonInput.placeholder = "e.g. Adjusted for late audit"
    reasonInput.style.cssText = [
      "position:absolute",
      `left:${elementLeft}px`,
      `top:${elementTop + height + 6 + reasonLabelHeight}px`,
      `width:${width}px`,
      "font:12px sans-serif",
      "color:#111827",
      "background:rgba(255,255,255,0.96)",
      "border:2px dashed #003d68",
      "border-radius:2px",
      "outline:none",
      "padding:3px 6px",
      "box-sizing:border-box",
      "z-index:200",
    ].join(";")
    parent.appendChild(reasonInput)

    this._activeEditor = { textarea, reasonLabel, reasonInput, target, valueNode, slideIndex, elementIndex }
    textarea.focus()
    textarea.select()

    // Blur handling: only commit once BOTH fields have lost focus, not the
    // instant the content textarea blurs (which would fire before a click
    // into the reason field ever registers, since blur precedes the new
    // focus event) -- a short delay lets a focus move between the two
    // sibling fields settle before deciding neither is still active.
    const onBlur = () => {
      setTimeout(() => {
        if (!this._activeEditor) return
        const active = document.activeElement
        if (active === textarea || active === reasonInput) return
        this._commit()
      }, 0)
    }
    textarea.addEventListener("blur", onBlur)
    reasonInput.addEventListener("blur", onBlur)

    textarea.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.stopPropagation()
        this._cancel()
      }
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
        textarea.blur()
      }
    })
    reasonInput.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.stopPropagation()
        this._cancel()
      }
      if (e.key === "Enter") {
        e.preventDefault()
        reasonInput.blur()
      }
    })
  }

  _cancel() {
    this._removeActiveEditor()
  }

  async _commit() {
    const editor = this._activeEditor
    if (!editor) return

    const { textarea, reasonInput, target, valueNode, slideIndex, elementIndex } = editor
    const content = textarea.value
    const original = valueNode.textContent
    const reason = reasonInput.value
    const originalReason = target.dataset.overrideReason || ""

    this._removeActiveEditor()

    // No-op guard: nothing changed from the pre-fill values (a right-click,
    // look, and click-away with nothing typed or amended) — skip the PATCH
    // entirely. Trimmed comparison because _openEditor pre-fills the textarea
    // from valueNode.textContent.trim(), so an unedited commit's value is the
    // trimmed form even when the rendered original carried incidental
    // whitespace. Reason is checked too (card #2324) so a reason-only edit
    // (content untouched) still commits rather than being treated as a
    // no-op look-and-leave.
    if (content === original.trim() && reason === originalReason) return

    // Blank-content guard (deck #28 incident, 2026-07-08): a fully-cleared
    // commit is never persisted — DecksController#update_element no-ops on
    // blank content rather than writing (or clobbering an existing entry to)
    // `{ "content" => "" }`, so there is nothing worth round-tripping to the
    // server here either. Revert the display straight back to whatever was
    // really there instead of showing a blank the server would never actually
    // save. The server-side guard is the real safety net (it also covers
    // AiTools::SetDeckElementOverrideService and any other write path); this
    // is purely a "don't bother" optimisation that keeps the on-screen state
    // honest.
    if (content.trim() === "") {
      valueNode.textContent = original
      return
    }

    // Optimistic update — the author sees their edit land immediately; a
    // failed save reverts it and flags the error rather than leaving a
    // silently-unsaved change on screen. Written onto valueNode (card #2407:
    // for a stat visual, that's ONLY the value div — target.textContent =
    // would replace the whole card's children, wiping out the label div
    // sitting beside it; for every other kind valueNode === target, so this
    // is unchanged behaviour there).
    valueNode.textContent = content

    try {
      const response = await fetch(this._updateUrl(slideIndex, elementIndex), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": this._csrfToken(),
        },
        body: JSON.stringify({ content, reason }),
      })

      if (!response.ok) throw new Error(`save failed: ${response.status}`)

      const data = await response.json()
      if (data.element_id) target.dataset.elementId = data.element_id
      // Keep the reason pre-fill in sync so a SECOND edit in the same page
      // view (no reload) still opens with whatever reason was just saved,
      // matching what a fresh visit would render server-side.
      target.dataset.overrideReason = reason
      this._flash(target, "success")
      this._syncMeasuresRow(slideIndex, elementIndex, data, reason)
    } catch (error) {
      valueNode.textContent = original
      this._flash(target, "error")
    }
  }

  // Sync the Measures tab's row after a canvas edit (card #2322 follow-up) --
  // the mirror image of deck_measures_row_controller.js#_syncCanvasElement.
  // Both tabs render once on the SAME page load (decks/show.html.erb just
  // toggles `hidden` per panel via persist-tabs), so a canvas edit needs to
  // reach into the Measures row's own DOM too, not just update itself.
  // slideIndex/elementIndex here are the raw strings straight off
  // target.dataset (DecksHelper#deck_element_edit_attrs), which line up
  // directly against the data-deck-measures-row-slide-index-value /
  // -element-index-value Stimulus Values the Measures row carries (card
  // #2322) -- Stimulus renders those as plain numeric strings in the DOM.
  // No-ops when this element isn't the CURRENT representative row for its
  // slot (Deck#bound_slots_summary only ever lists one row per slot) -- there
  // is nothing in the Measures tab for a non-representative element's edit
  // to update.
  _syncMeasuresRow(slideIndex, elementIndex, data, reason) {
    const row = document.querySelector(
      `[data-deck-measures-row-slide-index-value="${slideIndex}"][data-deck-measures-row-element-index-value="${elementIndex}"]`
    )
    if (!row) return

    const valueText = row.querySelector('[data-deck-measures-row-target="valueText"]')
    if (valueText) valueText.textContent = data.content

    // Keep the Measures row's own reason pre-fill in sync too (card #2324) --
    // a follow-up edit from that tab in the same page view should open with
    // whatever reason was just saved here, not a stale/blank one.
    const reasonInput = row.querySelector('[data-deck-measures-row-target="reasonInput"]')
    if (reasonInput) reasonInput.value = reason

    const badge = row.querySelector('[data-deck-measures-row-target="badge"] span')
    if (badge) badge.classList.remove("hidden")

    // Always-visible reason line + hover-reveal wiring (card #2324
    // follow-up) -- the SAME reveal-on-save treatment deck_measures_row_
    // controller.js#save applies to itself, mirrored here for a canvas-
    // originated edit reaching INTO the Measures tab instead.
    const reasonText = row.querySelector('[data-deck-measures-row-target="reasonText"]')
    if (reasonText) {
      reasonText.textContent = reason
      reasonText.title = reason
      reasonText.classList.toggle("hidden", !reason)
    }
    if (valueText) {
      const message = reason
        ? `Manually overridden: ${reason}`
        // PARITY: DecksHelper::DECK_OVERRIDE_LOCK_MESSAGE
        : "Manually overridden — protected from the next data Pull"
      valueText.setAttribute("data-controller", "deck-override-reason-hovercard")
      valueText.setAttribute("data-deck-override-reason-hovercard-message-value", message)
      valueText.setAttribute(
        "data-action",
        "mouseenter->deck-override-reason-hovercard#show mouseleave->deck-override-reason-hovercard#hide"
      )
    }
  }

  _updateUrl(slideIndex, elementIndex) {
    return `/decks/${this.deckIdValue}/element/${slideIndex}/${elementIndex}`
  }

  _csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  _removeActiveEditor() {
    if (this._activeEditor?.textarea?.isConnected) this._activeEditor.textarea.remove()
    if (this._activeEditor?.reasonLabel?.isConnected) this._activeEditor.reasonLabel.remove()
    if (this._activeEditor?.reasonInput?.isConnected) this._activeEditor.reasonInput.remove()
    this._activeEditor = null
  }

  // A visible save-confirmation cue on the edited element itself (there is no
  // separate save-icon slot on the show page the way the build editor has a
  // dedicated saveButton to flash) — a brief ring pulse, green for success,
  // red for a reverted failure. Classes/keyframes live in application.css.
  _flash(target, state) {
    const className = `deck-show-editor-flash-${state}`
    target.classList.remove(className)
    void target.offsetWidth // force reflow so a rapid repeat edit restarts the animation
    target.classList.add(className)
    target.addEventListener("animationend", () => target.classList.remove(className), { once: true })
  }
}
